import { useMutation, useQueryClient } from '@tanstack/react-query'

import { API_URL } from '@/constants/env'

import { SweetspotError } from '@/helpers/error'

import { libraryCompaniesQueryKeys } from '@/hooks/library/types'
import { markEntitiesAsConfirmedInRelationships } from '@/hooks/library/useEntityRelationships'
import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'

import type { PaginatedResponse } from '@/types/Api'
import type { UUID } from '@/types/Id'
import type { EntityCompany } from '@/types/library/Entity'

interface ConfirmCompaniesPayload {
  companyIds: UUID[]
}

const confirmCompanies = async (
  orgId: UUID,
  payload: ConfirmCompaniesPayload,
  accessToken: string,
) => {
  for (const companyId of payload.companyIds) {
    const response = await fetch(
      `${API_URL}/api/v1/organizations/${orgId}/entities/${companyId}/confirm`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )
    if (!response.ok) {
      throw new SweetspotError({
        name: 'FETCH_ERROR',
        message: response.statusText,
        cause: response,
      })
    }
  }
}

interface UseConfirmCompaniesArgs {
  orgId: UUID | ''
  optimisticUpdate?: boolean
}

export const useConfirmCompanies = ({
  orgId,
  optimisticUpdate = false,
}: UseConfirmCompaniesArgs) => {
  const {
    accessToken,
    user: { userId },
  } = useAuthInfoWithThrow()

  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: ConfirmCompaniesPayload) => {
      if (!orgId) {
        throw new Error('orgId is required')
      }
      return confirmCompanies(orgId, payload, accessToken)
    },
    onMutate: async (payload) => {
      if (!orgId) return
      if (!optimisticUpdate) return
      const key = libraryCompaniesQueryKeys.organization({ orgId })
      await queryClient.cancelQueries({ queryKey: key })
      const previousData =
        queryClient.getQueryData<PaginatedResponse<EntityCompany>>(key)
      if (previousData) {
        const updatedData = previousData.data.map((c) => {
          if (payload.companyIds.includes(c.id)) {
            return {
              ...c,
              confirmed: true,
              confirmed_by_user_id: userId as UUID,
            }
          }
          return c
        })
        queryClient.setQueryData<PaginatedResponse<EntityCompany>>(key, {
          ...previousData,
          data: updatedData,
        })
      }
      return { previousData }
    },
    onSuccess: (data, payload) => {
      if (!orgId) return

      // Mark confirmed entities in entity relationship queries
      markEntitiesAsConfirmedInRelationships(
        queryClient,
        payload.companyIds,
        userId as UUID,
      )

      return queryClient.invalidateQueries({
        queryKey: libraryCompaniesQueryKeys.organization({ orgId }),
      })
    },
  })
}
